/**
 * Shared data and derivations for the composition playground.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { SDPS } from "../../data/sdps.js";
import {
  composeGraded,
  makeGradedBridge,
  type BridgeBody,
  type CompositionVerdict,
  type GradedBridge,
} from "../../algorithms/bridge.js";
import {
  PHASES,
  singletonGrade,
  type Phase,
  type SdpCode,
} from "../../algorithms/grade.js";

interface SdpMeta {
  readonly code: SdpCode;
  readonly name: string;
  readonly color: string;
}

export const SDP_META: readonly SdpMeta[] = SDPS.map((s) => ({
  code: s.code,
  name: s.name,
  color: s.color,
}));

export const SDP_COLOR: Record<SdpCode, string> = ((): Record<
  SdpCode,
  string
> => {
  const out = {} as Record<SdpCode, string>;
  for (const s of SDP_META) out[s.code] = s.color;
  return out;
})();

export const PHASE_LABEL: Record<Phase, string> = {
  eta: "η · grant",
  epsilon: "ε · discharge",
};

export interface BridgeChoice {
  readonly sdp: SdpCode;
  readonly phase: Phase;
}

interface Preset {
  readonly id: string;
  readonly label: string;
  readonly accent: string;
  readonly b1: BridgeChoice | null;
  readonly b2: BridgeChoice | null;
  readonly note: string;
}

export const PRESETS: readonly Preset[] = [
  {
    id: "preset-case-a",
    label: "Replay (Case A) · [Dt, η] ∘ [Dt, η]",
    accent: "#ef4444",
    b1: { sdp: "Dt", phase: "eta" },
    b2: { sdp: "Dt", phase: "eta" },
    note: "Two identical grades ⇒ ℤ/2 self-cancels ⇒ composite = 0_G ⇒ T1 refusal.",
  },
  {
    id: "preset-case-b",
    label: "ICG envelope (Case B) · [Pa, η] ∘ [Pa, ε]",
    accent: "#14b8a6",
    b1: { sdp: "Pa", phase: "eta" },
    b2: { sdp: "Pa", phase: "epsilon" },
    note: "Same SDP, opposite phases ⇒ ICG-Pa envelope grade.",
  },
  {
    id: "preset-case-c",
    label: "Cross-SDP (Case C) · [Dt, η] ∘ [Tr, ε]",
    accent: "#22d3ee",
    b1: { sdp: "Dt", phase: "eta" },
    b2: { sdp: "Tr", phase: "epsilon" },
    note: "Distinct SDPs ⇒ multi-component composite ⇒ admissible only at cross-SDP seam.",
  },
  {
    id: "preset-reset",
    label: "Reset",
    accent: "#7a8aa6",
    b1: { sdp: "Dt", phase: "eta" },
    b2: { sdp: "Pa", phase: "epsilon" },
    note: "Default starting position.",
  },
];

export const SDP_CHIPS = SDP_META.map((s) => ({
  id: s.code,
  label: `${s.code} · ${s.name}`,
  color: s.color,
}));

export const PHASE_CHIPS_FOR = (
  color: string,
): readonly {
  readonly id: Phase;
  readonly label: string;
  readonly color: string;
}[] =>
  PHASES.map((phase) => ({
    id: phase,
    label: PHASE_LABEL[phase],
    color,
  }));

interface FourCaseRow {
  readonly id: "A" | "B" | "C" | "D";
  readonly title: string;
  readonly trigger: string;
  readonly outcome: string;
  readonly accent: string;
}

export const FOUR_CASES: readonly FourCaseRow[] = [
  {
    id: "A",
    title: "Case A · Replay",
    trigger: "g₁ = g₂  (same SDP, same phase)",
    outcome: "ℤ/2 self-cancels ⇒ composite = 0_G ⇒ T1 refusal",
    accent: "#ef4444",
  },
  {
    id: "B",
    title: "Case B · ICG envelope",
    trigger: "same SDP, opposite phases",
    outcome: "composite = [s, η] + [s, ε] = ICG-s envelope grade",
    accent: "#14b8a6",
  },
  {
    id: "C",
    title: "Case C · Cross-SDP",
    trigger: "distinct SDPs",
    outcome:
      "composite spans two components ⇒ admissible only at cross-SDP seam",
    accent: "#22d3ee",
  },
  {
    id: "D",
    title: "Case D · Vacuous",
    trigger: "either input bridge sits at 0_G (or has uninhabited license)",
    outcome: "T1 says the input is not a typable bridge ⇒ composition vacuous",
    accent: "#7a8aa6",
  },
];

export function buildBridge(choice: BridgeChoice): GradedBridge {
  const body: BridgeBody = {
    id: `body-${choice.sdp}-${choice.phase}`,
    witnesses: [{ sdp: choice.sdp, phase: choice.phase }],
  };
  return makeGradedBridge(body, singletonGrade(choice.sdp, choice.phase));
}

export interface CaseSummary {
  readonly caseId: "A" | "B" | "C" | "D";
  readonly outcome: "COMPOSED" | "REFUSED";
  readonly accent: string;
  readonly icg: string;
  readonly tag: string;
  readonly explanation: string;
}

export function summarize(verdict: CompositionVerdict): CaseSummary {
  if (verdict.outcome === "COMPOSED") {
    if (verdict.caseLabel === "B (ICG envelope)") {
      const sdp = verdict.icgSdp;
      return {
        caseId: "B",
        outcome: "COMPOSED",
        accent: sdp ? SDP_COLOR[sdp] : "#14b8a6",
        icg: verdict.icg ?? "ICG-?",
        tag: "Case B · ICG envelope",
        explanation: verdict.explanation,
      };
    }
    return {
      caseId: "C",
      outcome: "COMPOSED",
      accent: "#22d3ee",
      icg: "—",
      tag: "Case C · cross-SDP",
      explanation: verdict.explanation,
    };
  }
  if (verdict.caseLabel === "A (replay)") {
    return {
      caseId: "A",
      outcome: "REFUSED",
      accent: "#ef4444",
      icg: "—",
      tag: "Case A · replay refused by T1",
      explanation: verdict.explanation,
    };
  }
  return {
    caseId: "D",
    outcome: "REFUSED",
    accent: "#7a8aa6",
    icg: "—",
    tag: "Case D · vacuous",
    explanation: verdict.explanation,
  };
}

export { composeGraded };
